import { API_BASE_URL } from '../config/environment';
import { compressImageFile } from '../utils/imageUtils';

// Helper function to get admin auth token
const getAdminToken = (): string | null => {
  return localStorage.getItem('adminToken');
};

// Helper function to set admin auth headers
const getAdminHeaders = (): { [key: string]: string } => {
  const token = getAdminToken();
  return token ? { 
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  } : {
    'Content-Type': 'application/json'
  };
};

export interface DashboardStats {
  overview: {
    totalUsers: number;
    totalProducts: number;
    totalOrders: number;
    totalRevenue: number;
    pendingOrders: number;
  };
  recentOrders: any[];
  topProducts: any[];
  userStats: any[];
}

export interface AdminProduct {
  id: number;
  name: string;
  description?: string;
  price: number;
  originalPrice?: number;
  images: string[];
  hoverImage?: string;
  size?: string;
  inStock: boolean;
  categoryId?: number;
  categoryIds?: number[];
  category?: string | {
    id: number;
    name: string;
  };
  sustainabilityScore?: number;
  measurements?: { [key: string]: string };
  stockQuantity: number;
  productType?: string;
  status?: 'draft' | 'published' | 'archived';
  condition?: string;
  fitReferenceId?: number | null;
  isSold?: boolean;
}

export interface FitReferenceMeasurement {
  label: string;
  value: string;
}

export interface FitReference {
  id: number;
  name: string;
  type: 'dressform' | 'model';
  measurements: FitReferenceMeasurement[];
}

export interface AdminOrder {
  id: number;
  orderNumber: string;
  status: string;
  totalAmount: number;
  user: {
    firstName: string;
    lastName: string;
    email: string;
  };
  orderItems: {
    id: number;
    quantity: number;
    price: number;
    product: {
      name: string;
      images: string[];
    };
  }[];
  createdAt: string;
}

export interface AdminUser {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  isActive: boolean;
  createdAt: string;
  _count: {
    orders: number;
  };
}

export const adminApi = {
  // Image upload function
  async uploadImage(file: File): Promise<{ imageUrl: string; filename: string }> {
    console.log('📸 Admin Upload: Uploading image:', file.name, 'size:', file.size);
    
    const formData = new FormData();
    formData.append('image', await compressImageFile(file));
    
    const token = getAdminToken();
    const headers: { [key: string]: string } = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    
    // TRY API FIRST - Using new Cloudinary upload endpoint
    let response;
    try {
      response = await fetch(`${API_BASE_URL}/upload/image`, {
        method: 'POST',
        headers,
        body: formData, // Don't set Content-Type, let browser set it for FormData
      });
      console.log('✅ Upload API response:', response.status);
    } catch (apiError) {
      console.log('❌ Upload API failed, trying direct backend call...');
      // FALLBACK: Direct backend call for mobile
      const fallbackHeaders: { [key: string]: string } = {};
      if (token) {
        fallbackHeaders.Authorization = `Bearer ${token}`;
      }
      
      response = await fetch(`${API_BASE_URL}/upload/image`, {
        method: 'POST',
        headers: fallbackHeaders,
        body: formData,
      });
      console.log('✅ Upload direct response:', response.status);
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Upload error response:', errorText);
      
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText || `HTTP ${response.status}` };
      }
      
      throw new Error(errorData.error || errorData.message || 'Failed to upload image');
    }

    const result = await response.json();
    console.log('📸 Upload successful:', result);
    
    // Transform Cloudinary response to match expected format
    return {
      imageUrl: result.url,
      filename: result.publicId || result.url.split('/').pop()
    };
  },

  // Dashboard
  async getDashboardStats(): Promise<DashboardStats> {
    console.log('📊 Admin Dashboard: Loading stats...');
    
    // TRY API FIRST
    let response;
    try {
      response = await fetch(`${API_BASE_URL}/admin/dashboard/stats`, {
        headers: getAdminHeaders(),
      });
      console.log('✅ Dashboard API response:', response.status);
    } catch (apiError) {
      console.log('❌ Dashboard API failed, trying direct backend call...');
      // FALLBACK: Direct backend call for mobile
      response = await fetch(`${API_BASE_URL}/admin/dashboard/stats`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
      });
      console.log('✅ Dashboard direct response:', response.status);
    }

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch dashboard stats');
    }

    const result = await response.json();
    console.log('📊 Dashboard data loaded:', result);
    return result;
  },

  // Products Management
  async getProducts(params?: {
    page?: number;
    limit?: number;
    search?: string;
    category?: string;
  }) {
    console.log('🛍️ Admin Products: Loading products...');
    
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.category) queryParams.append('category', params.category);
    queryParams.append('includeStatus', 'all'); // Admin sees all products

    // TRY API FIRST
    let response;
    try {
      response = await fetch(`${API_BASE_URL}/admin/products?${queryParams}`, {
        headers: getAdminHeaders(),
      });
      console.log('✅ Products API response:', response.status);
    } catch (apiError) {
      console.log('❌ Products API failed, trying direct backend call...');
      // FALLBACK: Direct backend call for mobile
      response = await fetch(`${API_BASE_URL}/admin/products?${queryParams}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
      });
      console.log('✅ Products direct response:', response.status);
    }

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch products');
    }

    return response.json();
  },

  async createProduct(productData: Partial<AdminProduct>) {
    console.log('➕ Admin Create Product: Creating product:', productData.name);
    
    // TRY API FIRST
    let response;
    try {
      response = await fetch(`${API_BASE_URL}/admin/products`, {
        method: 'POST',
        headers: getAdminHeaders(),
        body: JSON.stringify(productData),
      });
      console.log('✅ Create Product API response:', response.status);
    } catch (apiError) {
      console.log('❌ Create Product API failed, trying direct backend call...');
      // FALLBACK: Direct backend call for mobile
      response = await fetch(`${API_BASE_URL}/admin/products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify(productData),
      });
      console.log('✅ Create Product direct response:', response.status);
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Create Product error response:', errorText);
      
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText || `HTTP ${response.status}` };
      }
      
      throw new Error(errorData.error || errorData.message || 'Failed to create product');
    }

    const result = await response.json();
    console.log('➕ Product created successfully:', result);
    return result;
  },

  async updateProduct(id: number, productData: Partial<AdminProduct>) {
    // Create a clean payload for the backend
    const updateData = {
      name: productData.name,
      description: productData.description || '',
      price: productData.price,
      originalPrice: productData.originalPrice,
      size: productData.size || '',
      category: productData.category,
      categoryIds: productData.categoryIds,
      images: productData.images || [],
      // '' clears the hover image; undefined leaves it untouched
      hoverImage: productData.hoverImage,
      stockQuantity: productData.stockQuantity,
      productType: productData.productType || undefined,
      condition: productData.condition,
      fitReferenceId: productData.fitReferenceId,
      isSold: productData.isSold
    };
    
    try {
      const response = await fetch(`${API_BASE_URL}/admin/products/${id}`, {
        method: 'PUT',
        headers: getAdminHeaders(),
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const responseText = await response.text();
        
        let errorData;
        try {
          errorData = JSON.parse(responseText);
        } catch {
          errorData = { error: responseText || `HTTP ${response.status} ${response.statusText}` };
        }
        
        const error = new Error(errorData.error || errorData.message || `Failed to update product (${response.status})`);
        (error as any).response = { data: errorData };
        throw error;
      }

      const result = await response.json();
      
      // If measurements were provided, save them separately
      if ((productData as any).measurements) {
        try {
          await this.saveProductMeasurements(id, (productData as any).measurements);
        } catch (error) {
          console.error('Failed to save measurements:', error);
        }
      }
      
      return result;
      
    } catch (fetchError) {
      
      // If it's a network error, provide helpful message
      if (fetchError instanceof Error && (fetchError.message === 'Failed to fetch' || fetchError.name === 'TypeError')) {
        throw new Error('Unable to connect to server. Please ensure the backend is running on port 3001.');
      }
      
      throw fetchError;
    }
  },

  // Fit references (dressforms / models)
  async getFitReferences(): Promise<FitReference[]> {
    const response = await fetch(`${API_BASE_URL}/admin/fit-references`, {
      headers: getAdminHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to load fit references');
    }

    return response.json();
  },

  async saveFitReference(fitReference: Omit<FitReference, 'id'> & { id?: number }): Promise<FitReference> {
    const response = await fetch(
      `${API_BASE_URL}/admin/fit-references${fitReference.id ? `/${fitReference.id}` : ''}`,
      {
        method: fitReference.id ? 'PUT' : 'POST',
        headers: getAdminHeaders(),
        body: JSON.stringify(fitReference),
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to save fit reference');
    }

    return response.json();
  },

  async deleteFitReference(id: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/admin/fit-references/${id}`, {
      method: 'DELETE',
      headers: getAdminHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to delete fit reference');
    }
  },

  async getProductMeasurements(productId: number): Promise<{ [key: string]: string }> {
    const response = await fetch(`${API_BASE_URL}/admin/products/${productId}/measurements`, {
      headers: getAdminHeaders(),
    });

    if (!response.ok) {
      console.error('Failed to fetch measurements');
      return {};
    }

    return response.json();
  },

  async saveProductMeasurements(productId: number, measurements: { [key: string]: string }): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/admin/products/${productId}/measurements`, {
      method: 'PUT',
      headers: getAdminHeaders(),
      body: JSON.stringify({ measurements }),
    });

    if (!response.ok) {
      throw new Error('Failed to save measurements');
    }
  },

  async deleteProduct(id: number) {
    console.log('🗑️ Admin Delete Product: Deleting product ID:', id);
    
    // TRY API FIRST
    let response;
    try {
      response = await fetch(`${API_BASE_URL}/admin/products/${id}`, {
        method: 'DELETE',
        headers: getAdminHeaders(),
      });
      console.log('✅ Delete Product API response:', response.status);
    } catch (apiError) {
      console.log('❌ Delete Product API failed, trying direct backend call...');
      // FALLBACK: Direct backend call for mobile
      response = await fetch(`${API_BASE_URL}/admin/products/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
      });
      console.log('✅ Delete Product direct response:', response.status);
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Delete Product error response:', errorText);
      
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText || `HTTP ${response.status}` };
      }
      
      throw new Error(errorData.error || errorData.message || 'Failed to delete product');
    }

    const result = await response.json();
    console.log('🗑️ Product deleted successfully:', result);
    return result;
  },

  async updateProductStatus(id: number, status: string) {
    console.log(`📝 Admin Update Product Status: Product ${id} to ${status}`);
    
    // TRY API FIRST
    let response;
    try {
      response = await fetch(`${API_BASE_URL}/admin/products/${id}/status`, {
        method: 'PATCH',
        headers: getAdminHeaders(),
        body: JSON.stringify({ status })
      });
      console.log('✅ Update Status API response:', response.status);
    } catch (apiError) {
      console.log('❌ Update Status API failed, trying direct backend call...');
      // FALLBACK: Direct backend call for mobile
      response = await fetch(`${API_BASE_URL}/admin/products/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify({ status })
      });
      console.log('✅ Update Status direct response:', response.status);
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Update Status error response:', errorText);
      
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText || `HTTP ${response.status}` };
      }
      
      throw new Error(errorData.error || errorData.message || 'Failed to update product status');
    }

    const result = await response.json();
    console.log(`📝 Product status updated successfully:`, result);
    return result;
  },

  // Orders Management
  async getOrders(params?: {
    page?: number;
    limit?: number;
    status?: string;
  }) {
    console.log('📦 Admin Orders: Loading orders...');
    
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);

    // Call the correct orders endpoint
    let response;
    try {
      response = await fetch(`${API_BASE_URL}/orders/admin/all?${queryParams}`, {
        headers: getAdminHeaders(),
      });
      console.log('✅ Orders API response:', response.status);
    } catch (apiError) {
      console.log('❌ Orders API failed, trying direct backend call...');
      // FALLBACK: Direct backend call for mobile
      response = await fetch(`${API_BASE_URL}/orders/admin/all?${queryParams}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
      });
      console.log('✅ Orders direct response:', response.status);
    }

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch orders');
    }

    return response.json();
  },

  async updateOrderStatus(id: number, status: string) {
    
    try {
      const response = await fetch(`${API_BASE_URL}/orders/${id}/status`, {
        method: 'PUT',
        headers: getAdminHeaders(),
        body: JSON.stringify({ status }),
      });

      if (!response.ok) {
        const responseText = await response.text();
        
        let errorData;
        try {
          errorData = JSON.parse(responseText);
        } catch {
          errorData = { error: responseText || `HTTP ${response.status} ${response.statusText}` };
        }
        
        throw new Error(errorData.error || errorData.message || `Failed to update order status (${response.status})`);
      }

      const result = await response.json();
      return result;
      
    } catch (fetchError) {
      
      if (fetchError instanceof Error && (fetchError.message === 'Failed to fetch' || fetchError.name === 'TypeError')) {
        throw new Error('Unable to connect to server. Please ensure the backend is running on port 3001.');
      }
      
      throw fetchError;
    }
  },

  async deleteOrder(id: number) {
    console.log('🗑️ Admin Delete Order: Deleting order ID:', id);
    
    // TRY API FIRST
    let response;
    try {
      response = await fetch(`${API_BASE_URL}/orders/${id}`, {
        method: 'DELETE',
        headers: getAdminHeaders(),
      });
      console.log('✅ Delete Order API response:', response.status);
    } catch (apiError) {
      console.log('❌ Delete Order API failed, trying direct backend call...');
      // FALLBACK: Direct backend call for mobile
      response = await fetch(`${API_BASE_URL}/orders/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
      });
      console.log('✅ Delete Order direct response:', response.status);
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Delete Order error response:', errorText);
      
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText || `HTTP ${response.status}` };
      }
      
      throw new Error(errorData.error || errorData.message || 'Failed to delete order');
    }

    const result = await response.json();
    console.log('🗑️ Order deleted successfully:', result);
    return result;
  },

  // Users Management
  async getUsers(params?: {
    page?: number;
    limit?: number;
    search?: string;
  }) {
    console.log('👥 Admin Users: Loading users...');
    
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);

    // TRY API FIRST
    let response;
    try {
      response = await fetch(`${API_BASE_URL}/admin/users?${queryParams}`, {
        headers: getAdminHeaders(),
      });
      console.log('✅ Users API response:', response.status);
    } catch (apiError) {
      console.log('❌ Users API failed, trying direct backend call...');
      // FALLBACK: Direct backend call for mobile
      response = await fetch(`${API_BASE_URL}/admin/users?${queryParams}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
      });
      console.log('✅ Users direct response:', response.status);
    }

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch users');
    }

    return response.json();
  },

  async updateUserStatus(id: number, isActive: boolean) {
    const response = await fetch(`${API_BASE_URL}/admin/users/${id}/status`, {
      method: 'PUT',
      headers: getAdminHeaders(),
      body: JSON.stringify({ isActive }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update user status');
    }

    return response.json();
  },

  // Content Management - Banners
  async getBanners() {
    const response = await fetch(`${API_BASE_URL}/admin/banners`, {
      headers: getAdminHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch banners');
    }

    return response.json();
  },

  async createBanner(bannerData: any) {
    const response = await fetch(`${API_BASE_URL}/admin/banners`, {
      method: 'POST',
      headers: getAdminHeaders(),
      body: JSON.stringify(bannerData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create banner');
    }

    return response.json();
  },

  async updateBanner(id: number, bannerData: any) {
    const response = await fetch(`${API_BASE_URL}/admin/banners/${id}`, {
      method: 'PUT',
      headers: getAdminHeaders(),
      body: JSON.stringify(bannerData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update banner');
    }

    return response.json();
  },

  async deleteBanner(id: number) {
    const response = await fetch(`${API_BASE_URL}/admin/banners/${id}`, {
      method: 'DELETE',
      headers: getAdminHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete banner');
    }

    return response.json();
  },

  async updateBannerStatus(id: number, isActive: boolean) {
    const response = await fetch(`${API_BASE_URL}/admin/banners/${id}/status`, {
      method: 'PUT',
      headers: getAdminHeaders(),
      body: JSON.stringify({ isActive }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update banner status');
    }

    return response.json();
  },

  // Content Management - Site Settings
  async getSiteSettings() {
    const response = await fetch(`${API_BASE_URL}/admin/site-settings`, {
      headers: getAdminHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch site settings');
    }

    return response.json();
  },

  async updateSiteSettings(settingsData: any) {
    const response = await fetch(`${API_BASE_URL}/admin/site-settings`, {
      method: 'PUT',
      headers: getAdminHeaders(),
      body: JSON.stringify(settingsData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update site settings');
    }

    return response.json();
  },

  // Homepage Content Management (Legacy - keeping for backward compatibility)
  async getHomepageContent() {
    const response = await fetch(`${API_BASE_URL}/admin/homepage-content`, {
      headers: getAdminHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch homepage content');
    }

    return response.json();
  },

  async updateHomepageContent(id: number, contentData: any) {
    const response = await fetch(`${API_BASE_URL}/admin/homepage-content/${id}`, {
      method: 'PUT',
      headers: getAdminHeaders(),
      body: JSON.stringify(contentData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update homepage content');
    }

    return response.json();
  },

  // Outfit Management
  async getOutfits(params?: { includeStatus?: string }) {
    console.log('👗 Admin Outfits: Loading outfits...');
    
    const queryParams = new URLSearchParams();
    if (params?.includeStatus) queryParams.append('includeStatus', params.includeStatus);

    // TRY API FIRST
    let response;
    try {
      response = await fetch(`${API_BASE_URL}/admin/shop-the-look?${queryParams}`, {
        headers: getAdminHeaders(),
      });
      console.log('✅ Outfits API response:', response.status);
    } catch (apiError) {
      console.log('❌ Outfits API failed, trying direct backend call...');
      // FALLBACK: Direct backend call for mobile
      response = await fetch(`${API_BASE_URL}/admin/shop-the-look?${queryParams}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
      });
      console.log('✅ Outfits direct response:', response.status);
    }

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch outfits');
    }

    return response.json();
  },

  async createOutfit(outfitData: any) {
    console.log('➕ Admin Create Outfit: Creating outfit:', outfitData.title);
    
    // TRY API FIRST
    let response;
    try {
      response = await fetch(`${API_BASE_URL}/admin/shop-the-look`, {
        method: 'POST',
        headers: getAdminHeaders(),
        body: JSON.stringify(outfitData),
      });
      console.log('✅ Create Outfit API response:', response.status);
    } catch (apiError) {
      console.log('❌ Create Outfit API failed, trying direct backend call...');
      // FALLBACK: Direct backend call for mobile
      response = await fetch(`${API_BASE_URL}/admin/shop-the-look`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify(outfitData),
      });
      console.log('✅ Create Outfit direct response:', response.status);
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Create Outfit error response:', errorText);
      
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText || `HTTP ${response.status}` };
      }
      
      throw new Error(errorData.error || errorData.message || 'Failed to create outfit');
    }

    const result = await response.json();
    console.log('➕ Outfit created successfully:', result);
    return result;
  },

  async deleteOutfit(id: number) {
    console.log('🗑️ Admin Delete Outfit: Deleting outfit ID:', id);
    
    // TRY API FIRST
    let response;
    try {
      response = await fetch(`${API_BASE_URL}/admin/shop-the-look/${id}`, {
        method: 'DELETE',
        headers: getAdminHeaders(),
      });
      console.log('✅ Delete Outfit API response:', response.status);
    } catch (apiError) {
      console.log('❌ Delete Outfit API failed, trying direct backend call...');
      // FALLBACK: Direct backend call for mobile
      response = await fetch(`${API_BASE_URL}/admin/shop-the-look/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
      });
      console.log('✅ Delete Outfit direct response:', response.status);
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Delete Outfit error response:', errorText);
      
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText || `HTTP ${response.status}` };
      }
      
      throw new Error(errorData.error || errorData.message || 'Failed to delete outfit');
    }

    const result = await response.json();
    console.log('🗑️ Outfit deleted successfully:', result);
    return result;
  },

  async updateOutfit(id: number, outfitData: any) {
    console.log('📝 Admin Update Outfit: Updating outfit ID:', id);
    
    // TRY API FIRST
    let response;
    try {
      response = await fetch(`${API_BASE_URL}/admin/shop-the-look/${id}`, {
        method: 'PUT',
        headers: getAdminHeaders(),
        body: JSON.stringify(outfitData),
      });
      console.log('✅ Update Outfit API response:', response.status);
    } catch (apiError) {
      console.log('❌ Update Outfit API failed, trying direct backend call...');
      // FALLBACK: Direct backend call for mobile
      response = await fetch(`${API_BASE_URL}/admin/shop-the-look/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify(outfitData),
      });
      console.log('✅ Update Outfit direct response:', response.status);
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Update Outfit error response:', errorText);
      
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText || `HTTP ${response.status}` };
      }
      
      throw new Error(errorData.error || errorData.message || 'Failed to update outfit');
    }

    const result = await response.json();
    console.log('📝 Outfit updated successfully:', result);
    return result;
  },

  async updateOutfitStatus(id: number, status: string) {
    console.log(`👗 Admin Update Outfit Status: Outfit ${id} to ${status}`);
    
    // TRY API FIRST
    let response;
    try {
      response = await fetch(`${API_BASE_URL}/admin/shop-the-look/${id}/status`, {
        method: 'PATCH',
        headers: getAdminHeaders(),
        body: JSON.stringify({ status })
      });
      console.log('✅ Update Outfit Status API response:', response.status);
    } catch (apiError) {
      console.log('❌ Update Outfit Status API failed, trying direct backend call...');
      // FALLBACK: Direct backend call for mobile
      response = await fetch(`${API_BASE_URL}/admin/shop-the-look/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify({ status })
      });
      console.log('✅ Update Outfit Status direct response:', response.status);
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Update Outfit Status error response:', errorText);
      
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText || `HTTP ${response.status}` };
      }
      
      throw new Error(errorData.error || errorData.message || 'Failed to update outfit status');
    }

    const result = await response.json();
    console.log(`👗 Outfit status updated successfully:`, result);
    return result;
  },
};